import React from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const SOURCE_COLORS = {
  web: '#66b86a',
  android: '#4699e7',
  ios: '#5b63bf'
};

const RADIAN = Math.PI / 180;

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent === 0) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="#ffffff" textAnchor="middle" dominantBaseline="central" fontSize={13} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const { name, value, color } = payload[0];
  return (
    <div style={{
      background: 'rgba(17, 28, 48, 0.94)',
      border: '1px solid rgba(110, 137, 178, 0.45)',
      borderRadius: 10,
      padding: '9px 12px',
      color: '#d8e4f8',
      fontSize: 12
    }}>
      <strong style={{ color }}>{name}</strong>: {value}
    </div>
  );
};

export default class AppointmentSourceChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = { data: [], loading: true };
  }

  componentDidMount() {
    this.fetchData();
  }

  // PE-438: the statistics page appends its filter query; without the prop the
  // dashboard keeps fetching the bare endpoint exactly as before.
  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) this.fetchData();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  fetchData() {
    // Rapid filter changes can resolve out of order; only the latest request
    // may write state. Inert on the dashboard, where query never changes.
    const requestId = (this.requestId = (this.requestId || 0) + 1);
    this.setState({ loading: true });
    fetch('/api/appointments/source_chart' + (this.props.query || ''))
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ data, loading: false });
      })
      .catch(() => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ loading: false });
      });
  }

  render() {
    const { data, loading } = this.state;

    const chartData = loading ? [] : data
      .filter(item => item.value > 0)
      .map(item => ({
        name: item.label,
        value: item.value,
        color: SOURCE_COLORS[item.id] || '#90A4AE'
      }));

    return (
      <div>
        {loading ? (
          <div style={{ height: 300, padding: '12px 0' }}>
            <Skeleton height={260} borderRadius={14} />
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
            No data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="46%"
                outerRadius={94}
                label={renderCustomLabel}
                labelLine={false}
                paddingAngle={1}
              >
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="circle"
                align="center"
                verticalAlign="bottom"
                iconSize={9}
                wrapperStyle={{ paddingTop: 6 }}
                formatter={(value) => <span style={{ color: '#546682', fontSize: 13 }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }
}
